import React, { useState, useEffect, useRef } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Ticket, Home, Heart, User, Store, ArrowLeft, MessageSquare, Sparkles } from "lucide-react";
import { api, authHeaders } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ClientAuthDialog from "@/components/ClientAuthDialog";
import LocationSheet from "@/components/LocationSheet";
import InboxBell from "@/components/InboxBell";
import OnboardingTutorial, { tutorialDone, resetTutorial } from "@/components/OnboardingTutorial";
import CompleteClientProfile from "@/components/CompleteClientProfile";
import PendingSpendGate from "@/components/PendingSpendGate";
import Confetti from "@/components/Confetti";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ShieldAlert } from "lucide-react";

const tabs = [
  { to: "/", label: "Offerte", icon: Home, testid: "nav-offers" },
  { to: "/coupons", label: "Coupon", icon: Ticket, testid: "nav-coupons" },
  { to: "/favorites", label: "Preferiti", icon: Heart, testid: "nav-favorites" },
  { to: "/account", label: "Profilo", icon: User, testid: "nav-account" },
];

export default function ClientLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { client, refreshClient } = useAuth();
  const needsProfile = !!client && (!client.birth_year || !client.gender);
  const [authOpen, setAuthOpen] = useState(false);
  const [locOpen, setLocOpen] = useState(false);
  const [tutOpen, setTutOpen] = useState(false);
  const [msgUnread, setMsgUnread] = useState(0);
  const couponTabRef = useRef(null);
  const [fly, setFly] = useState(null);
  const [couponPulse, setCouponPulse] = useState(false);
  const [couponCount, setCouponCount] = useState(0);
  const [blocked, setBlocked] = useState(null);

  const fetchCouponCount = React.useCallback(async () => {
    if (!client) { setCouponCount(0); return; }
    try {
      const r = await api.get("/client/claims", { headers: authHeaders("client") });
      const n = (r.data.claims || []).filter((c) => !c.redeemed && !c.expired).length;
      setCouponCount(n);
    } catch (_) {}
  }, [client]);

  useEffect(() => { fetchCouponCount(); }, [fetchCouponCount]);
  useEffect(() => { if (pathname === "/coupons") fetchCouponCount(); }, [pathname, fetchCouponCount]);

  useEffect(() => {
    const onChanged = () => fetchCouponCount();
    const onBlocked = (e) => setBlocked({ message: e.detail?.message || "", until: e.detail?.suspendedUntil || null });
    window.addEventListener("kn-coupons-changed", onChanged);
    window.addEventListener("kn-coupon-blocked", onBlocked);
    return () => { window.removeEventListener("kn-coupons-changed", onChanged); window.removeEventListener("kn-coupon-blocked", onBlocked); };
  }, [fetchCouponCount]);

  useEffect(() => {
    const onClaim = (e) => {
      const tab = couponTabRef.current;
      const rect = tab ? tab.getBoundingClientRect() : null;
      const tx = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
      const ty = rect ? rect.top + rect.height / 2 : window.innerHeight - 30;
      const sx = e.detail?.x ?? window.innerWidth / 2;
      const sy = e.detail?.y ?? window.innerHeight / 2;
      setFly({ sx, sy, dx: tx - sx, dy: ty - sy, label: e.detail?.label || "", claimId: e.detail?.claimId || null, phase: "start", landed: false });
      requestAnimationFrame(() => requestAnimationFrame(() =>
        setFly((f) => (f ? { ...f, phase: "end" } : f))
      ));
      setTimeout(() => {
        setFly((f) => (f ? { ...f, landed: true } : f));
        setCouponPulse(true);
        setCouponCount((c) => c + 1);
        try { if (navigator.vibrate) navigator.vibrate([18, 40, 22]); } catch (_) {}
      }, 1150);
      setTimeout(() => setCouponPulse(false), 2600);
      setTimeout(() => setFly(null), 3400);
      setTimeout(fetchCouponCount, 1800);
    };
    window.addEventListener("kn-coupon-claimed", onClaim);
    return () => window.removeEventListener("kn-coupon-claimed", onClaim);
  }, [fetchCouponCount]);

  useEffect(() => {
    if (!client) { setMsgUnread(0); return; }
    let active = true;
    const loadUnread = async () => {
      try { const r = await api.get("/client/messages/unread", { headers: authHeaders("client") }); if (active) setMsgUnread(r.data.unread || 0); } catch (_) {}
    };
    loadUnread();
    const t = setInterval(loadUnread, 30000);
    const onRead = () => setMsgUnread(0);
    window.addEventListener("kn-messages-read", onRead);
    return () => { active = false; clearInterval(t); window.removeEventListener("kn-messages-read", onRead); };
  }, [client]);

  useEffect(() => {
    if (pathname === "/" && !tutorialDone("client") && !needsProfile) {
      const t = setTimeout(() => setTutOpen(true), 900);
      return () => clearTimeout(t);
    }
  }, [pathname, needsProfile]);

  useEffect(() => {
    const replay = () => { resetTutorial("client"); navigate("/"); setTimeout(() => setTutOpen(true), 400); };
    window.addEventListener("kn-replay-tutorial", replay);
    return () => window.removeEventListener("kn-replay-tutorial", replay);
  }, [navigate]);

  const goTab = (t) => {
    if ((t.to === "/coupons" || t.to === "/account" || t.to === "/favorites") && !client) {
      setAuthOpen(true);
      return;
    }
    navigate(t.to);
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center gap-3">
          {pathname !== "/" && (
            <button
              onClick={() => { if (window.history.length > 1) navigate(-1); else navigate("/"); }}
              data-testid="back-button"
              aria-label="Indietro"
              className="h-10 w-10 -ml-1 rounded-full hover:bg-slate-100 flex items-center justify-center shrink-0 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-slate-700" />
            </button>
          )}
          <button onClick={() => navigate("/")} data-testid="brand-home" className="flex items-center gap-2 shrink-0">
            <div className="h-9 w-9 rounded-xl kn-gradient flex items-center justify-center">
              <Ticket className="h-5 w-5 text-white" />
            </div>
            <span className="font-extrabold text-lg tracking-tight text-slate-900 hidden sm:block">
              Koupo<span className="text-[#E63946]">Now</span>
            </span>
          </button>

          <div className="ml-auto flex items-center gap-3">
            {client && (
              <button
                data-testid="messages-button"
                onClick={() => navigate("/messages")}
                title="Messaggi"
                className="relative h-10 w-10 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center shrink-0 transition-colors"
              >
                <MessageSquare className="h-5 w-5 text-slate-600" />
                {msgUnread > 0 && (
                  <span data-testid="messages-unread" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full kn-gradient text-white text-[10px] font-extrabold flex items-center justify-center">
                    {msgUnread > 9 ? "9+" : msgUnread}
                  </span>
                )}
              </button>
            )}

            <InboxBell role="client" />

            <button
              data-testid="profile-avatar-link"
              onClick={() => { if (client) navigate("/account"); else setAuthOpen(true); }}
              title={client ? "Il tuo profilo" : "Accedi"}
              className="h-10 w-10 rounded-full overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center hover:ring-2 hover:ring-[#E63946]/30 transition-all shrink-0"
            >
              {client?.photo ? <img src={client.photo} alt="" className="h-full w-full object-cover" /> : <User className="h-5 w-5 text-slate-500" />}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-4">
        <Outlet context={{ openAuth: () => setAuthOpen(true), openLocation: () => setLocOpen(true) }} />
      </main>

      {/* Bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 z-30 bg-white/90 backdrop-blur-xl border-t border-slate-100">
        <div className="max-w-3xl mx-auto grid grid-cols-4">
          {tabs.map((t) => {
            const active = pathname === t.to;
            const Icon = t.icon;
            const isCoupon = t.to === "/coupons";
            return (
              <button
                key={t.to}
                ref={isCoupon ? couponTabRef : null}
                data-testid={t.testid}
                onClick={() => goTab(t)}
                className={`relative flex flex-col items-center gap-0.5 py-2.5 text-[11px] font-semibold transition-colors ${
                  active ? "text-[#E63946]" : (isCoupon && couponPulse ? "text-[#E63946]" : "text-slate-400")
                }`}
              >
                <span className="relative">
                  <Icon className={`h-5 w-5 transition-transform ${active ? "fill-[#FFE4E6]" : ""} ${isCoupon && couponPulse ? "kn-coupon-bump fill-[#FFE4E6]" : ""}`} />
                  {isCoupon && couponPulse && (
                    <span className="absolute -inset-2 rounded-full kn-coupon-ring pointer-events-none" />
                  )}
                  {isCoupon && client && couponCount > 0 && (
                    <span data-testid="nav-coupons-count" className="absolute -top-1.5 -right-2 min-w-[17px] h-[17px] px-1 rounded-full kn-gradient text-white text-[10px] font-extrabold flex items-center justify-center ring-2 ring-white">
                      {couponCount > 9 ? "9+" : couponCount}
                    </span>
                  )}
                  {t.testid === "nav-account" && msgUnread > 0 && (
                    <span data-testid="nav-account-dot" className="absolute -top-1 -right-1.5 h-2.5 w-2.5 rounded-full bg-[#E63946] ring-2 ring-white" />
                  )}
                </span>
                {t.label}
              </button>
            );
          })}
        </div>
      </nav>

      <ClientAuthDialog open={authOpen} onOpenChange={setAuthOpen} />
      <LocationSheet open={locOpen} onOpenChange={setLocOpen} />
      {tutOpen && <OnboardingTutorial role="client" onClose={() => setTutOpen(false)} />}
      {needsProfile && <CompleteClientProfile client={client} onDone={refreshClient} />}
      <PendingSpendGate />

      {/* Coupon claim celebration */}
      <style>{`
        @keyframes kn-save-pop{0%{transform:translate(-50%,-50%) scale(.6);opacity:0}45%{transform:translate(-50%,-50%) scale(1.06);opacity:1}100%{transform:translate(-50%,-50%) scale(1);opacity:1}}
        @keyframes kn-coupon-bump{0%{transform:scale(1)}30%{transform:scale(1.45)}55%{transform:scale(.9)}100%{transform:scale(1)}}
        @keyframes kn-coupon-ring{0%{transform:scale(.4);opacity:.7}100%{transform:scale(1.6);opacity:0}}
        .kn-coupon-bump{animation:kn-coupon-bump .6s ease both}
        .kn-coupon-ring{background:radial-gradient(circle, rgba(230,57,70,.35) 0%, rgba(230,57,70,0) 70%);animation:kn-coupon-ring .9s ease-out infinite}
      `}</style>
      {fly && (
        <div className="fixed inset-0 z-[2600] pointer-events-none" data-testid="coupon-fly-overlay">
          <div
            className="absolute left-1/2 top-1/3 text-center"
            style={{ transform: "translate(-50%,-50%)", animation: "kn-save-pop .5s ease both" }}
          >
            <div className="inline-flex flex-col items-center gap-1 px-6 py-4 rounded-3xl bg-white/95 backdrop-blur kn-shadow-soft border border-[#FFE4E6]">
              <Sparkles className="h-7 w-7 text-[#E63946]" />
              <p className="text-lg font-extrabold text-slate-900">Stai risparmiando!</p>
              {fly.label && <span className="text-2xl font-black text-[#E63946] leading-none">{fly.label}</span>}
              <p className="text-xs text-slate-400 font-semibold mt-0.5">Salvato nei tuoi Coupon</p>
              {fly.claimId && (
                <button
                  data-testid="coupon-fly-view"
                  onClick={() => { const cid = fly.claimId; setFly(null); navigate(`/coupon/${cid}`); }}
                  className="pointer-events-auto mt-2 h-9 px-5 rounded-full kn-gradient text-white text-sm font-extrabold"
                >
                  Vedi
                </button>
              )}
            </div>
          </div>
          {!fly.landed && (
            <div
              style={{
                position: "fixed", left: fly.sx, top: fly.sy, willChange: "transform, opacity",
                transform: fly.phase === "end"
                  ? `translate(calc(-50% + ${fly.dx}px), calc(-50% + ${fly.dy}px)) scale(.22) rotate(14deg)`
                  : "translate(-50%,-50%) scale(1) rotate(-4deg)",
                opacity: fly.phase === "end" ? 0.25 : 1,
                transition: "transform .95s cubic-bezier(.5,.05,.2,1), opacity .95s ease-in",
              }}
            >
              <div className="h-14 w-14 rounded-2xl kn-gradient text-white flex items-center justify-center kn-shadow-soft">
                <Ticket className="h-7 w-7" />
              </div>
            </div>
          )}
        </div>
      )}
      {fly && !fly.landed && <Confetti pieces={36} onDone={() => {}} />}

      <Dialog open={!!blocked} onOpenChange={(o) => { if (!o) setBlocked(null); }}>
        <DialogContent className="sm:max-w-sm rounded-3xl" data-testid="coupon-blocked-dialog">
          <div className="text-center pt-2">
            <div className="h-14 w-14 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-3">
              <ShieldAlert className="h-7 w-7 text-amber-600" />
            </div>
            <h3 className="text-lg font-extrabold text-slate-900">Richiesta temporaneamente sospesa</h3>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{blocked?.message}</p>
            <button
              data-testid="coupon-blocked-ok"
              onClick={() => { setBlocked(null); navigate("/coupons"); }}
              className="mt-4 w-full h-11 rounded-xl kn-gradient text-white font-bold"
            >
              Vai ai miei coupon
            </button>
            <button onClick={() => setBlocked(null)} className="mt-2 w-full h-10 rounded-xl text-slate-500 font-bold text-sm">Ho capito</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
